import { memo } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import useSWRMutation from 'swr/mutation';

import { MainScreenSlideCard } from './mainScreenSlideCard/MainScreenSlideCard';

import css from './DraggableSlideCards.module.css';

import { mainScreenSettingsService } from 'v2/api/mainScreenSettingsService';
import { SortableCard } from 'v2/components/card/sortableCard/SortableCard';
import { useSortableList } from 'v2/hooks/useSortableList';
import { SlideModel } from 'v2/types/SlideModel';

interface DraggableSlideCardsProps {
  slides: SlideModel[];
  onRevalidate: VoidFunction;
}

export const DraggableSlideCards = memo<DraggableSlideCardsProps>(({ slides, onRevalidate }) => {
  const { items, itemIds, sensors, activeItem, onDragStart, onDragEnd, onDragCancel } = useSortableList<SlideModel>(
    slides,
    {
      onReorder: (next) => {
        trigger(next.map((item, i) => ({ id: item.id, order: i + 1 })));
      },
    }
  );

  const { trigger } = useSWRMutation('changeSlideOrder', mainScreenSettingsService.changeSlideOrder, {
    onSuccess: () => {
      onRevalidate();
    },
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={itemIds}>
        <div className={css.draggableSlides}>
          {items.map((item) => (
            <SortableCard key={item.id} id={item.id}>
              <MainScreenSlideCard slide={item} onRevalidate={onRevalidate} />
            </SortableCard>
          ))}
        </div>
      </SortableContext>

      <DragOverlay adjustScale style={{ cursor: 'grabbing' }}>
        {activeItem && <MainScreenSlideCard slide={activeItem} onRevalidate={onRevalidate} />}
      </DragOverlay>
    </DndContext>
  );
});

DraggableSlideCards.displayName = 'DraggableSlideCards';
